/**
 * SponsorsManager — handles Sponsors.json
 * Data shape: { sponsors: [{ path: "https://..." }] }
 */
import React, { Component } from 'react';
import AdminLayout from '../AdminLayout.jsx';
import { fetchDataFile, saveDataFile, uploadPhotoFile } from '../../../APICall/Api.jsx';

const FILE = 'Sponsors.json';
const EMPTY = { path: '' };

class SponsorsManager extends Component {
  state = {
    rawData: null,
    items: [],
    loading: true,
    saving: false,
    error: null,
    modal: null,
    editIndex: null,
    formData: { ...EMPTY },
    uploadingImg: false,
    savedMsg: '',
  };

  componentDidMount() { this.loadData(); }

  loadData = async () => {
    this.setState({ loading: true, error: null });
    try {
      const data = await fetchDataFile(FILE);
      this.setState({ rawData: data, items: data.sponsors || [], loading: false });
    } catch (e) {
      this.setState({ error: 'Failed to load sponsors.', loading: false });
    }
  };

  saveData = async (newItems) => {
    this.setState({ saving: true });
    try {
      const { rawData } = this.state;
      const payload = { ...rawData, sponsors: newItems };
      await saveDataFile(FILE, payload);
      this.setState({ items: newItems, saving: false, savedMsg: 'Saved!' });
      setTimeout(() => this.setState({ savedMsg: '' }), 3000);
    } catch (e) {
      this.setState({ saving: false, error: 'Save failed. Check your admin token.' });
    }
  };

  openAdd  = () => this.setState({ modal: 'add', formData: { ...EMPTY }, editIndex: null });
  openEdit = (idx) => this.setState({ modal: 'edit', editIndex: idx, formData: { ...this.state.items[idx] } });
  closeModal = () => this.setState({ modal: null, formData: { ...EMPTY } });

  handleField = (key, val) =>
    this.setState((s) => ({ formData: { ...s.formData, [key]: val } }));

  handleImgUpload = async (file) => {
    this.setState({ uploadingImg: true });
    try {
      const res = await uploadPhotoFile(file);
      if (res.path) {
        const url = `https://buildgreenalbania.org/${res.path}`;
        this.setState((s) => ({
          formData: { ...s.formData, path: url },
          uploadingImg: false,
        }));
      }
    } catch (e) {
      this.setState({ uploadingImg: false });
    }
  };

  handleSave = () => {
    const { items, editIndex, formData, modal } = this.state;
    const newItems = modal === 'add'
      ? [...items, formData]
      : items.map((it, i) => (i === editIndex ? formData : it));
    this.saveData(newItems);
    this.closeModal();
  };

  handleDelete = (idx) => {
    if (!window.confirm('Remove this sponsor?')) return;
    const newItems = this.state.items.filter((_, i) => i !== idx);
    this.saveData(newItems);
  };

  render() {
    const { items, loading, saving, modal, formData, uploadingImg, savedMsg, error } = this.state;
    const { history, location } = this.props;

    return (
      <AdminLayout title="Manage Sponsors" history={history} location={location}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            {savedMsg && <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓ {savedMsg}</span>}
            {error && <span style={{ color: '#e53935' }}>{error}</span>}
          </div>
          <button className="bgg-btn bgg-btn-primary" onClick={this.openAdd}>
            <i className="fas fa-plus" /> Add Sponsor
          </button>
        </div>

        {/* Sponsor logo grid */}
        <div className="bgg-admin-card">
          <div className="bgg-admin-card-header">
            <h2>Sponsors ({loading ? '…' : items.length})</h2>
          </div>
          {loading
            ? <div style={{ padding: 32, textAlign: 'center', color: '#8a8aaa' }}>Loading…</div>
            : (
              <div style={{ padding: 24 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
                  {items.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        padding: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                        background: '#fafafa',
                      }}
                    >
                      {item.path
                        ? <img src={item.path} alt={`Sponsor ${idx + 1}`} style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
                        : <div style={{ height: 80, display: 'flex', alignItems: 'center', color: '#ccc', fontSize: 12 }}>No image</div>}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="bgg-btn bgg-btn-outline bgg-btn-sm" onClick={() => this.openEdit(idx)}>
                          <i className="fas fa-edit" />
                        </button>
                        <button className="bgg-btn bgg-btn-danger bgg-btn-sm" onClick={() => this.handleDelete(idx)}>
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add new placeholder */}
                  <div
                    style={{
                      border: '2px dashed #c8e6c9',
                      borderRadius: 8,
                      padding: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      color: '#66bb6a',
                      minHeight: 120,
                    }}
                    onClick={this.openAdd}
                  >
                    <i className="fas fa-plus-circle" style={{ fontSize: 28 }} />
                    <span style={{ fontSize: '0.82rem' }}>Add Sponsor</span>
                  </div>
                </div>

                {items.length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>
                    No sponsors yet. Click "Add Sponsor" to create one.
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Modal */}
        {modal && (
          <div className="bgg-modal-overlay" onClick={this.closeModal}>
            <div className="bgg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bgg-modal-header">
                <h3>{modal === 'add' ? 'Add Sponsor Logo' : 'Edit Sponsor Logo'}</h3>
                <button className="bgg-modal-close" onClick={this.closeModal}>×</button>
              </div>
              <div className="bgg-modal-body">
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Logo Image URL</label>
                  <input
                    className="bgg-form-input"
                    type="text"
                    value={formData.path || ''}
                    onChange={(e) => this.handleField('path', e.target.value)}
                    placeholder="Paste image URL or upload below"
                  />
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ fontSize: '0.82rem' }}
                      onChange={(e) => e.target.files[0] && this.handleImgUpload(e.target.files[0])}
                    />
                    {uploadingImg && <span style={{ color: '#8a8aaa', fontSize: '0.82rem' }}>Uploading…</span>}
                  </div>
                  {formData.path && (
                    <div style={{ marginTop: 12, padding: 12, background: '#f5f5f5', borderRadius: 8, textAlign: 'center' }}>
                      <img src={formData.path} alt="" style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }} />
                    </div>
                  )}
                </div>
              </div>
              <div className="bgg-modal-footer">
                <button className="bgg-btn bgg-btn-outline" onClick={this.closeModal}>Cancel</button>
                <button className="bgg-btn bgg-btn-primary" onClick={this.handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    );
  }
}

export default SponsorsManager;



// WEBPACK FOOTER //
// ./src/views/Admin/managers/SponsorsManager.jsx